/**
 * Phase 5Z.35 — System Backup & Restore (Operations-Portal)
 *
 * Single in-app management surface for full-system backups:
 *   • Status card (maintenance, last backup, storage path, provider)
 *   • Auto-backup settings (daily + weekly + retention)
 *   • Manual backup, ZIP upload, list with download / restore / delete
 *   • Transactional restore with "RESTORE"-typed confirmation +
 *     pre-restore safety snapshot (server side).
 *
 * Backend endpoints (see backend/routes/full_backup.py):
 *   GET    /api/admin/full-backup/list
 *   GET    /api/admin/full-backup/config
 *   PUT    /api/admin/full-backup/config
 *   POST   /api/admin/full-backup/create
 *   POST   /api/admin/full-backup/upload                  (multipart)
 *   POST   /api/admin/full-backup/download/{id}
 *   POST   /api/admin/full-backup/{id}/delete             (legacy, password)
 *   DELETE /api/admin/full-backup/{id}                    (system_admin)
 *   POST   /api/admin/full-backup/restore/{id}
 *   GET    /api/admin/maintenance/status
 *   POST   /api/admin/maintenance/toggle
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, Download, Upload, Trash2, RotateCcw, Loader2,
  RefreshCw, Save, Server, AlertTriangle, CheckCircle2, Clock,
  Power, FileArchive, Lock, PlayCircle,
} from "lucide-react";

const WEEKDAY_LABELS_DE = ["Montag", "Dienstag", "Mittwoch", "Donnerstag",
  "Freitag", "Samstag", "Sonntag"];

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let x = n;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-AT"); } catch { return iso; }
}

export default function SystemBackupRestorePanel() {
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [maint, setMaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null); // backup item
  const fileRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const [listRes, maintRes] = await Promise.all([
        api.get("/admin/full-backup/list"),
        api.get("/admin/maintenance/status").catch(() => ({ data: null })),
      ]);
      setItems(listRes.data.items || []);
      setConfig(listRes.data.config || null);
      setMaint(maintRes.data || { enabled: false });
    } catch (e) {
      const msg = e.response?.data?.detail || "Konnte System-Status nicht laden";
      toast.error(msg);
      setItems([]);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function promptPassword(verb) {
    const pw = window.prompt(
      `Backup-Passwort eingeben um fortzufahren (${verb}).\n\n` +
      "Hinweis: Diese Aktion exportiert sensible Daten inkl. " +
      "Datenbank-Inhalten und Umgebungsvariablen. Bewahre die ZIP " +
      "sicher auf — sie enthält Passwort-Hashes, API-Keys und alle " +
      "Geschäftsdaten."
    );
    return pw || null;
  }

  async function createNow() {
    const pw = await promptPassword("erstellen");
    if (!pw) return;
    setCreating(true);
    try {
      const { data } = await api.post("/admin/full-backup/create", { password: pw });
      toast.success(`Backup erstellt — ${fmtBytes(data.size_bytes)}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erstellung fehlgeschlagen");
    } finally { setCreating(false); }
  }

  async function uploadZip(file) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Nur .zip-Dateien erlaubt");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/full-backup/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Backup hochgeladen — ${fmtBytes(data.size_bytes)}`);
      await load();
    } catch (e) {
      const status = e.response?.status;
      let msg = e.response?.data?.detail || "Upload fehlgeschlagen";
      if (status === 413) msg = "Datei zu groß (max 2 GB)";
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function downloadOne(id) {
    const pw = await promptPassword("herunterladen");
    if (!pw) return;
    try {
      const res = await api.post(`/admin/full-backup/download/${encodeURIComponent(id)}`,
        { password: pw }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = id;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download gestartet");
    } catch (e) {
      const msg = e.response?.data?.detail
        || (e.response?.status === 401 ? "Falsches Passwort" : "Download fehlgeschlagen");
      toast.error(msg);
    }
  }

  async function deleteOne(id) {
    if (!window.confirm(`Backup "${id}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/full-backup/${encodeURIComponent(id)}`);
      toast.success("Backup gelöscht");
      await load();
    } catch (e) {
      // Fallback to the legacy password-gated delete (admin/GF without system_admin)
      const status = e.response?.status;
      if (status === 403) {
        const pw = await promptPassword("löschen");
        if (!pw) return;
        try {
          await api.post(`/admin/full-backup/${encodeURIComponent(id)}/delete`, { password: pw });
          toast.success("Backup gelöscht");
          await load();
          return;
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Löschen fehlgeschlagen");
          return;
        }
      }
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  async function saveConfig() {
    if (!config) return;
    setSavingConfig(true);
    try {
      const { data } = await api.put("/admin/full-backup/config", {
        weekly_enabled: !!config.weekly_enabled,
        weekday: Number(config.weekday) || 0,
        hour: Number(config.hour) || 3,
        daily_enabled: !!config.daily_enabled,
        daily_hour: Number(config.daily_hour) || 23,
        daily_minute: Number(config.daily_minute) || 59,
        keep_last: Math.max(1, Math.min(100, Number(config.keep_last) || 30)),
      });
      setConfig(data);
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSavingConfig(false); }
  }

  async function toggleMaintenance(next) {
    setToggling(true);
    try {
      let reason = null;
      if (next) {
        reason = window.prompt(
          "Wartungsmodus aktivieren?\n\n" +
          "Optional: Grund eingeben (wird im Audit-Log gespeichert)."
        );
        if (reason === null) { setToggling(false); return; } // user cancelled
      }
      const { data } = await api.post("/admin/maintenance/toggle", {
        enabled: next, reason: reason || undefined,
      });
      setMaint(data);
      toast.success(next ? "Wartungsmodus aktiviert" : "Wartungsmodus deaktiviert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally { setToggling(false); }
  }

  const lastBackup = items[0] || null;

  return (
    <div className="space-y-6" data-testid="system-backup-restore-panel">
      {/* ── Status card ───────────────────────────────────────────────── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-5" data-testid="system-status-card">
        <div className="flex items-center gap-2 mb-4">
          <Server className="w-4 h-4 text-blue-400" />
          <h3 className="font-display text-zinc-50 font-semibold">System-Status</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <StatTile
            label="Wartungsmodus"
            value={maint?.enabled ? "AKTIV" : "Aus"}
            color={maint?.enabled ? "text-amber-300" : "text-emerald-300"}
            testId="status-maintenance"
          />
          <StatTile
            label="Backups gesamt"
            value={items.length}
            color="text-zinc-200"
            testId="status-count"
          />
          <StatTile
            label="Letztes Backup"
            value={lastBackup ? fmtDate(lastBackup.created_at) : "—"}
            color="text-zinc-200"
            testId="status-last"
          />
          <StatTile
            label="Letzte Größe"
            value={lastBackup ? fmtBytes(lastBackup.size_bytes) : "—"}
            color="text-zinc-200"
            testId="status-size"
          />
        </div>
        {config && (
          <div className="mt-4 text-[11px] text-zinc-500 flex flex-wrap gap-x-6 gap-y-1">
            <span>Speicher: <span className="font-mono text-zinc-400">{config.storage_path || "—"}</span></span>
            <span>Provider: <span className="font-mono text-zinc-400">{config.provider || "local"}</span></span>
            <span>Behalte zuletzt: <span className="font-mono text-zinc-400">{config.keep_last}</span></span>
          </div>
        )}
        {/* Maintenance banner */}
        {maint?.enabled && (
          <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2.5 flex items-start gap-2"
               data-testid="maintenance-banner">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-200/90 flex-1">
              <div className="font-semibold">Wartungsmodus ist aktiviert</div>
              <div className="mt-0.5">
                Seit {fmtDate(maint.started_at)} von {maint.started_by || "—"}.
                {maint.reason ? <> · Grund: <span className="italic">{maint.reason}</span></> : null}
              </div>
            </div>
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => toggleMaintenance(!maint?.enabled)}
            disabled={toggling}
            data-testid="maintenance-toggle-btn"
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
              maint?.enabled
                ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                : "bg-amber-500 hover:bg-amber-400 text-zinc-950"
            } disabled:opacity-50`}
          >
            {toggling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Power className="w-3.5 h-3.5" />}
            {maint?.enabled ? "Wartungsmodus deaktivieren" : "Wartungsmodus aktivieren"}
          </button>
          <button onClick={load} title="Neu laden" data-testid="status-refresh"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-zinc-300 hover:text-zinc-100 bg-zinc-800 hover:bg-zinc-700 rounded">
            <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
          </button>
        </div>
      </div>

      {/* ── Automatic backup settings ─────────────────────────────────── */}
      {config && (
        <div className="bg-zinc-900 border border-zinc-800 rounded p-5" data-testid="auto-backup-card">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <h3 className="font-display text-zinc-50 font-semibold">Automatische Sicherung</h3>
          </div>
          <p className="text-[11px] text-zinc-500 mb-4">
            Tägliche Sicherung empfohlen um 23:59 Uhr. Wöchentliche Sicherung kann zusätzlich oder alternativ aktiviert werden.
            Die letzten <span className="font-mono text-zinc-300">{config.keep_last}</span> Backups werden automatisch behalten — ältere werden gelöscht.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Daily */}
            <div className="border border-zinc-800 rounded p-3 bg-zinc-950/40" data-testid="daily-config">
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                <input type="checkbox" checked={!!config.daily_enabled}
                       onChange={(e) => setConfig({ ...config, daily_enabled: e.target.checked })}
                       data-testid="daily-enabled" className="w-4 h-4 accent-amber-500" />
                <span className="font-semibold">Tägliches Backup aktivieren</span>
              </label>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Stunde</label>
                  <input type="number" min="0" max="23" value={config.daily_hour}
                         onChange={(e) => setConfig({ ...config, daily_hour: Number(e.target.value) })}
                         data-testid="daily-hour"
                         className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Minute</label>
                  <input type="number" min="0" max="59" value={config.daily_minute}
                         onChange={(e) => setConfig({ ...config, daily_minute: Number(e.target.value) })}
                         data-testid="daily-minute"
                         className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono" />
                </div>
              </div>
              {config.last_daily_run && (
                <div className="mt-2 text-[10px] text-zinc-500">
                  Letzter Lauf: <span className="font-mono text-zinc-400">{config.last_daily_run}</span>
                </div>
              )}
            </div>

            {/* Weekly */}
            <div className="border border-zinc-800 rounded p-3 bg-zinc-950/40" data-testid="weekly-config">
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                <input type="checkbox" checked={!!config.weekly_enabled}
                       onChange={(e) => setConfig({ ...config, weekly_enabled: e.target.checked })}
                       data-testid="weekly-enabled" className="w-4 h-4 accent-amber-500" />
                <span className="font-semibold">Wöchentliches Backup aktivieren</span>
              </label>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Wochentag</label>
                  <select value={config.weekday}
                          onChange={(e) => setConfig({ ...config, weekday: Number(e.target.value) })}
                          data-testid="weekly-weekday"
                          className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                    {WEEKDAY_LABELS_DE.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Stunde</label>
                  <input type="number" min="0" max="23" value={config.hour}
                         onChange={(e) => setConfig({ ...config, hour: Number(e.target.value) })}
                         data-testid="weekly-hour"
                         className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono" />
                </div>
              </div>
              {config.last_auto_run && (
                <div className="mt-2 text-[10px] text-zinc-500">
                  Letzter Lauf: <span className="font-mono text-zinc-400">{config.last_auto_run}</span>
                </div>
              )}
            </div>

            {/* Retention */}
            <div className="border border-zinc-800 rounded p-3 bg-zinc-950/40" data-testid="retention-config">
              <div className="text-xs text-zinc-300 font-semibold mb-3">Aufbewahrung</div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Behalte letzte N Backups</label>
              <input type="number" min="1" max="100" value={config.keep_last}
                     onChange={(e) => setConfig({ ...config, keep_last: Number(e.target.value) })}
                     data-testid="keep-last"
                     className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono" />
              <div className="mt-2 text-[10px] text-zinc-500">Empfohlen: 30 (täglich = 1 Monat Historie)</div>
            </div>
          </div>

          <button onClick={saveConfig} disabled={savingConfig} data-testid="save-config-btn"
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 rounded">
            {savingConfig ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Zeitplan speichern
          </button>
        </div>
      )}

      {/* ── Manual create / upload ────────────────────────────────────── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-5" data-testid="manual-card">
        <div className="flex items-center gap-2 mb-1">
          <FileArchive className="w-4 h-4 text-emerald-400" />
          <h3 className="font-display text-zinc-50 font-semibold">Manuelles Backup &amp; Upload</h3>
        </div>
        <p className="text-[11px] text-zinc-500 mb-4">
          Erstelle jederzeit ein vollständiges System-Backup oder lade ein zuvor heruntergeladenes Backup wieder hoch (z.B. zum Wiederherstellen auf einem anderen System).
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={createNow} disabled={creating} data-testid="manual-backup-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Jetzt manuelles Backup erstellen
          </button>

          <label className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded cursor-pointer ${uploading ? "bg-zinc-800 text-zinc-500" : "bg-blue-600 hover:bg-blue-500 text-white"}`}
                 data-testid="upload-backup-label">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Backup-ZIP hochladen
            <input ref={fileRef} type="file" accept=".zip,application/zip" className="hidden"
                   disabled={uploading}
                   onChange={(e) => uploadZip(e.target.files?.[0])}
                   data-testid="upload-backup-input" />
          </label>
        </div>
        <div className="mt-3 text-[11px] text-zinc-500">
          Max. Dateigröße: 2 GB · ZIP wird serverseitig validiert (manifest.json + DB-Dump erforderlich).
        </div>
      </div>

      {/* ── Backup list ───────────────────────────────────────────────── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-5" data-testid="backup-list-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileArchive className="w-4 h-4 text-zinc-400" />
            <h3 className="font-display text-zinc-50 font-semibold">Backup-Historie</h3>
          </div>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
            {items.length} Backup{items.length === 1 ? "" : "s"}
          </span>
        </div>
        {loading ? (
          <div className="text-xs text-zinc-500 py-6 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Lade…
          </div>
        ) : items.length === 0 ? (
          <div className="text-xs text-zinc-500 py-6 italic" data-testid="backup-list-empty">
            Noch keine Backups vorhanden. Klicke „Jetzt manuelles Backup erstellen" oder aktiviere die tägliche Sicherung.
          </div>
        ) : (
          <div className="border border-zinc-800 rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-3 py-2 text-left">Datei</th>
                  <th className="px-3 py-2 text-right">Größe</th>
                  <th className="px-3 py-2 text-left">Erstellt</th>
                  <th className="px-3 py-2 w-44 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {items.map(it => {
                  const isSafety = it.id.includes("_safety");
                  const isUpload = it.id.includes("_uploaded");
                  const isDaily = it.id.includes("_daily");
                  return (
                    <tr key={it.id} data-testid={`backup-row-${it.id}`} className="hover:bg-zinc-950/40">
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-200">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[280px]">{it.filename}</span>
                          {isSafety && <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30">Safety</span>}
                          {isUpload && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30">Upload</span>}
                          {isDaily && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">Daily</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-zinc-300 tabular-nums">{fmtBytes(it.size_bytes)}</td>
                      <td className="px-3 py-2 text-xs text-zinc-400">{fmtDate(it.created_at)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => downloadOne(it.id)} title="Herunterladen"
                                data-testid={`backup-dl-${it.id}`}
                                className="p-1.5 text-blue-400 hover:bg-blue-500/10 rounded">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setRestoreTarget(it)} title="Wiederherstellen"
                                data-testid={`backup-restore-${it.id}`}
                                className="p-1.5 text-amber-400 hover:bg-amber-500/10 rounded ml-1">
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteOne(it.id)} title="Löschen"
                                data-testid={`backup-del-${it.id}`}
                                className="p-1.5 text-rose-400 hover:bg-rose-500/10 rounded ml-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {restoreTarget && (
        <RestoreConfirmDialog
          backup={restoreTarget}
          maintEnabled={!!maint?.enabled}
          onClose={() => setRestoreTarget(null)}
          onCompleted={async () => { setRestoreTarget(null); await load(); }}
          onActivateMaintenance={() => toggleMaintenance(true)}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, color = "text-zinc-200", testId }) {
  return (
    <div className="bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2.5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-base font-mono font-bold tabular-nums mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}

function RestoreConfirmDialog({ backup, maintEnabled, onClose, onCompleted, onActivateMaintenance }) {
  const [step, setStep] = useState(maintEnabled ? 2 : 1);
  const [confirmText, setConfirmText] = useState("");
  const [includeFiles, setIncludeFiles] = useState(true);
  const [busy, setBusy] = useState(false);

  async function doRestore() {
    if (confirmText !== "RESTORE") {
      toast.error('Bitte „RESTORE" exakt eintippen');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/full-backup/restore/${encodeURIComponent(backup.id)}`,
        { confirm_text: "RESTORE", include_storage_files: includeFiles }
      );
      toast.success(
        `Restore abgeschlossen — ${data.documents_restored} Datensätze in ${data.collections_restored} Sammlungen wiederhergestellt`
      );
      onCompleted();
    } catch (e) {
      const status = e.response?.status;
      let msg = e.response?.data?.detail || "Restore fehlgeschlagen";
      if (status === 409) msg = "Wartungsmodus muss VOR dem Restore aktiviert werden";
      toast.error(msg);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid="restore-dialog">
      <div className="bg-zinc-900 border border-rose-500/40 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-400" />
            <h3 className="font-display text-zinc-50 font-semibold">System wiederherstellen</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="restore-close">✕</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-rose-500/10 border border-rose-500/30 rounded p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-rose-200/90 leading-relaxed">
              <div className="font-semibold mb-1">Achtung: Diese Aktion ersetzt aktuelle Systemdaten.</div>
              Die Datenbank wird durch den Inhalt des Backups <b>{backup.filename}</b> ersetzt (erstellt am {fmtDate(backup.created_at)}).
              Aktive Benutzer-Sessions können nach dem Restore ungültig werden.
              Vor dem Restore wird automatisch ein <b>Sicherheits-Snapshot</b> des aktuellen Zustands erstellt
              — falls etwas schiefgeht, kannst du auf diesen zurückrollen.
            </div>
          </div>

          {/* Step 1: maintenance precondition */}
          {step === 1 && (
            <div className="space-y-3" data-testid="restore-step-1">
              <div className="text-sm text-zinc-200 font-semibold">Schritt 1 / 2 · Wartungsmodus aktivieren</div>
              <p className="text-xs text-zinc-400">
                Bevor das System wiederhergestellt wird, muss der Wartungsmodus aktiviert sein.
                Damit werden während des Restores keine widersprüchlichen Schreibvorgänge ausgeführt.
              </p>
              <div className={`flex items-center gap-2 text-xs rounded px-3 py-2 border ${maintEnabled ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}>
                {maintEnabled ? <CheckCircle2 className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                {maintEnabled ? "Wartungsmodus ist bereits aktiv" : "Wartungsmodus ist derzeit deaktiviert"}
              </div>
              <div className="flex gap-2 pt-2">
                {!maintEnabled && (
                  <button onClick={onActivateMaintenance} data-testid="restore-activate-maint"
                          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-zinc-950 rounded">
                    <Power className="w-3.5 h-3.5" /> Wartungsmodus aktivieren
                  </button>
                )}
                <button onClick={() => setStep(2)} disabled={!maintEnabled} data-testid="restore-next-btn"
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded">
                  Weiter zu Schritt 2 →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: confirmation */}
          {step === 2 && (
            <div className="space-y-3" data-testid="restore-step-2">
              <div className="text-sm text-zinc-200 font-semibold">Schritt 2 / 2 · Bestätigung</div>

              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                <input type="checkbox" checked={includeFiles}
                       onChange={(e) => setIncludeFiles(e.target.checked)}
                       data-testid="restore-include-files"
                       className="w-4 h-4 accent-rose-500" />
                Storage-Dateien aus dem Backup ebenfalls wiederherstellen (Logos, Dokumente, Belege)
              </label>

              <div>
                <label className="block text-[11px] text-zinc-400 mb-1">
                  Zum Bestätigen bitte <span className="font-mono text-rose-300">RESTORE</span> eintippen:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  data-testid="restore-confirm-input"
                  placeholder="RESTORE"
                  className="w-full px-3 py-2 bg-zinc-950 border border-rose-500/40 rounded text-sm font-mono text-zinc-100 focus:outline-none focus:border-rose-500"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={() => setStep(1)} data-testid="restore-back-btn"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-zinc-300 hover:text-zinc-100 bg-zinc-800 hover:bg-zinc-700 rounded">
                  ← Zurück
                </button>
                <button
                  onClick={doRestore}
                  disabled={busy || confirmText !== "RESTORE" || !maintEnabled}
                  data-testid="restore-confirm-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                  System jetzt wiederherstellen
                </button>
                <button onClick={onClose} disabled={busy} data-testid="restore-cancel-btn"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-zinc-300 hover:text-zinc-100 bg-zinc-800 hover:bg-zinc-700 rounded ml-auto disabled:opacity-50">
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
